import React from 'react';

import {Anchor, Icon, Text} from '@albert-io/atomic';
import type {IconProp, IconStyle} from '@albert-io/atomic/atoms/Icon/Icon.react';

interface Props {
  icon?: IconProp;
  /** Leading icon weight (default 'regular'); main-content rows pass 'light'. */
  iconStyle?: IconStyle;
  /** Custom leading chip content (e.g. a brandmark). Overrides `icon`. */
  leading?: React.ReactNode;
  /** Domain-subtle chip classes for the leading icon (e.g. `background-kiwi-subtle content-kiwi`). */
  chipClassName: string;
  label: string;
  /** Trailing affordance; external links get an up-right arrow, actions pass their own (sync/plus). */
  trailingIcon?: IconProp;
  /** Renders an external link (`<a target="_blank">`). Provide this OR `onClick`. */
  href?: string;
  /** Renders an action (`<button>`). Provide this OR `href`. */
  onClick?: () => void;
  'data-testid'?: string;
}

const ROW_CLASS =
  'flex w-full items-center gap-x1-half rounded-x1-half border border-border-subtle background-primary p-x1-half content-primary hover:background-primary-hover active:background-primary-pressed';

/**
 * A single getting-started row
 */
export const GettingStartedItem = ({
  icon,
  iconStyle = 'regular',
  leading,
  chipClassName,
  label,
  trailingIcon = 'arrow-up-right',
  href,
  onClick,
  'data-testid': testId
}: Props) => {
  const inner = (
    <>
      <span
        className={`h-x4 w-x4 text-m flex shrink-0 items-center justify-center rounded-full ${chipClassName}`}
      >
        {leading ??
          (icon ? <Icon color='inherit' icon={icon} iconStyle={iconStyle} size='inherit' /> : null)}
      </span>
      <Text className='grow' size='s'>
        {label}
      </Text>
      <Icon
        className='content-tertiary shrink-0 text-xs'
        icon={trailingIcon}
        iconStyle='regular'
        size='inherit'
      />
    </>
  );

  if (href) {
    return (
      <Anchor
        as='a'
        className={ROW_CLASS}
        data-testid={testId}
        href={href}
        noUnderline
        rel='noopener noreferrer'
        target='_blank'
      >
        {inner}
      </Anchor>
    );
  }

  return (
    <button
      className={`${ROW_CLASS} cursor-pointer text-left`}
      data-testid={testId}
      onClick={onClick}
      type='button'
    >
      {inner}
    </button>
  );
};
